import React, { useState } from 'react';
import { Trophy, Target, Star, Crown, Globe, Users, Medal } from 'lucide-react';
import calendarService, {
  CalendarEvent,
  CalendarCategory,
} from '../../services/calendarService';
import { ACCENT } from './eventUtils';

interface EventThumbnailProps {
  event: CalendarEvent;
  category?: CalendarCategory | null;
  className?: string;
  /** Icon-Größe im Fallback-Platzhalter */
  iconSize?: number;
}

function iconForCategory(category?: CalendarCategory | null): React.ElementType {
  const hint = `${category?.icon || ''} ${category?.slug || ''} ${category?.name || ''}`.toLowerCase();
  if (hint.includes('trophy') || hint.includes('major') || hint.includes('turnier')) return Trophy;
  if (hint.includes('crown') || hint.includes('wm') || hint.includes('world')) return Crown;
  if (hint.includes('star') || hint.includes('premier')) return Star;
  if (hint.includes('globe') || hint.includes('international')) return Globe;
  if (hint.includes('medal') || hint.includes('liga') || hint.includes('league')) return Medal;
  if (hint.includes('users') || hint.includes('team')) return Users;
  return Target;
}

/**
 * TEMPORÄR: Einheitliches Platzhalter-Thumbnail für ALLE Kalender-Events
 * (User-Vorgabe 2026-07-02), bis echte Event-Bilder gepflegt sind.
 * Zum Zurückbauen: Konstante entfernen und wieder `event.image` verwenden.
 */
const TEMP_EVENT_THUMBNAIL = '/images/event-placeholder.webp';

/**
 * Event-Thumbnail mit robustem Fallback: Wenn kein Bild vorhanden ist oder
 * das Bild nicht lädt, wird ein Gradient-Platzhalter mit Kategorie-Icon
 * gerendert – niemals ein kaputtes Bild.
 */
const EventThumbnail: React.FC<EventThumbnailProps> = ({
  event,
  category,
  className = '',
  iconSize = 28,
}) => {
  const [broken, setBroken] = useState(false);
  const url = TEMP_EVENT_THUMBNAIL || calendarService.buildFileUrl(event.image);
  const tint = category?.color || ACCENT;

  if (!url || broken) {
    const Icon = iconForCategory(category);
    return (
      <div
        className={`flex items-center justify-center overflow-hidden ${className}`}
        style={{
          background: `linear-gradient(135deg, ${tint}2E 0%, #18181b 55%, #09090b 100%)`,
        }}
        aria-hidden="true"
      >
        <div
          className="flex items-center justify-center rounded-2xl p-3"
          style={{ backgroundColor: `${tint}1F`, color: tint }}
        >
          <Icon size={iconSize} />
        </div>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={event.title || 'Event'}
      loading="lazy"
      onError={() => setBroken(true)}
      className={`object-cover ${className}`}
    />
  );
};

export default EventThumbnail;
